import { useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";
import * as THREE from "three";

// Premium "editorial atlas" palette: jewel-tone fills tuned for a dark-blue
// stage. Lower luminance + low saturation reads richer than rainbow brights.
const PALETTE = [
  "#2f6a5a", // deep emerald
  "#2a5d8f", // sapphire
  "#7a3e63", // wine
  "#b88a3a", // burnished gold
  "#4a5d6e", // gunmetal
  "#8a4a3a", // oxblood
  "#2d6f7a", // peacock
  "#5a4b8a", // royal indigo
  "#a07a3f", // bronze
  "#4a7458", // moss
  "#6d4670", // dusk plum
  "#1f5a73", // deep cyan
  "#9a6a44", // copper
  "#3f5a7c", // dusk blue
  "#8a5a78", // mauve
  "#345f47", // pine
  "#7a5a3a", // taupe gold
  "#3e4f6d", // slate ink
];

// Cheap, deterministic string hash → palette index.
function colorFor(key) {
  if (!key) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffffffff;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function isoOf(feat) {
  const p = feat?.properties || {};
  // Natural Earth provides ISO_A2 / ISO_A2_EH; fall back to NAME for deterministic hashing.
  const iso = p.ISO_A2 && p.ISO_A2 !== "-99" ? p.ISO_A2 : p.ISO_A2_EH;
  return (iso && iso !== "-99" ? iso : (p.NAME || p.name || "")).toLowerCase();
}

function nameOf(feat) {
  return feat?.properties?.NAME || feat?.properties?.name || "Unknown";
}

// Build a premium, cinematic letter texture: metallic platinum core, layered
// bloom halo, crisp inner highlight. Cached by char+size.
const TEXTURE_CACHE = new Map();
function letterTexture(char, fontPx) {
  const key = `${char}|${fontPx}`;
  if (TEXTURE_CACHE.has(key)) return TEXTURE_CACHE.get(key);
  const size = 512; // generous canvas so the glow halo and serifs don't clip
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, size, size);
  ctx.font = `900 ${fontPx}px Cinzel, "Helvetica Neue", serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const cx = size / 2, cy = size / 2;

  // Pass 1 — wide, cool platinum bloom
  ctx.shadowColor = "rgba(180,215,255,0.85)";
  ctx.shadowBlur = 70;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(char, cx, cy);

  // Pass 2 — tighter inner halo
  ctx.shadowColor = "rgba(220,235,255,0.95)";
  ctx.shadowBlur = 24;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(char, cx, cy);

  // Pass 3 — metallic platinum gradient core
  ctx.shadowBlur = 0;
  const grad = ctx.createLinearGradient(0, cy - fontPx * 0.45, 0, cy + fontPx * 0.45);
  grad.addColorStop(0.00, "#ffffff");
  grad.addColorStop(0.42, "#f4f8ff");
  grad.addColorStop(0.58, "#dde6f5");
  grad.addColorStop(1.00, "#aab9d0");
  ctx.fillStyle = grad;
  ctx.fillText(char, cx, cy);

  // Pass 4 — bright top-half specular sheen
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, size, cy - fontPx * 0.08);
  ctx.clip();
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.fillText(char, cx, cy);
  ctx.restore();

  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 16;
  tex.needsUpdate = true;
  TEXTURE_CACHE.set(key, tex);
  return tex;
}

// Place a single letter as a flat plane tangent to the sphere surface so it
// wraps with the curvature instead of billboarding to the camera.
function makeLetterMesh(char, fontPx, planeSize) {
  const tex = letterTexture(char, fontPx);
  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const geo = new THREE.PlaneGeometry(planeSize, planeSize);
  const mesh = new THREE.Mesh(geo, mat);
  // Render after the globe so transparent edges blend cleanly on top.
  mesh.renderOrder = 10;
  return mesh;
}

// Lay out a string of characters along a small-circle of the sphere (constant lat).
function layoutWord(word, centerLng, spacingDeg, lat) {
  const span = (word.length - 1) * spacingDeg;
  const start = centerLng - span / 2;
  return word.split("").map((ch, i) => ({
    char: ch,
    lat,
    lng: start + i * spacingDeg,
  }));
}

// Position + orient a plane to sit on the globe at (lat, lng). The plane is
// rotated only around the world Y-axis so the text stays vertically upright
// (no forward/back lean from sphere curvature). It still wraps horizontally
// around the globe — only the pitch is locked to 0.
function placeOnSphere(mesh, lat, lng, globe, altitude) {
  const { x, y, z } = globe.getCoords(lat, lng, altitude);
  mesh.position.set(x, y, z);
  mesh.rotation.set(0, Math.atan2(x, z), 0);
}

export default function HeroGlobe() {
  const globeRef = useRef(null);
  const wrapRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [size, setSize] = useState(420);

  // Deeper, slightly metallic ocean — gives the jewel-tone landmasses more
  // contrast and a "premium atlas" feel.
  const globeMaterial = useMemo(
    () => new THREE.MeshPhongMaterial({
      color: "#06182f",
      opacity: 0.98,
      transparent: true,
      shininess: 26,
      specular: new THREE.Color("#1e3a5f"),
    }),
    []
  );
  useEffect(() => () => globeMaterial.dispose?.(), [globeMaterial]);

  useEffect(() => {
    let cancelled = false;
    // Natural Earth Admin 0 (110m) — includes ISO_A2 codes for flag lookups.
    fetch("https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson")
      .then(r => r.json())
      .then(geo => {
        if (cancelled) return;
        const feats = (geo?.features || []).filter(f => (f?.properties?.NAME || "") !== "Antarctica");
        setCountries(feats);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const w = el.clientWidth;
      setSize(Math.max(300, Math.min(620, w)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    // Cinematic entrance: start zoomed-out, then ease in over ~2.4s.
    g.pointOfView({ lat: 0, lng: -150, altitude: 4.8 }, 0);
    g.pointOfView({ lat: 0, lng: -150, altitude: 2.1 }, 2400);
    const controls = g.controls();
    if (controls) {
      controls.autoRotate = true;
      // Slower, cinematic rotation suits the premium aesthetic.
      controls.autoRotateSpeed = 0.42;
      controls.enableZoom = true;
      controls.enableDamping = true;
      controls.dampingFactor = 0.12;
    }
  }, [countries.length]);

  // Pause rotation while a country is hovered, resume on leave.
  useEffect(() => {
    const g = globeRef.current;
    const controls = g?.controls?.();
    if (controls) controls.autoRotate = !hovered;
  }, [hovered]);

  // Mount the 3D wrapped-text labels onto the globe's THREE scene.
  // The text is parented to a Group anchored at world origin — as the camera
  // auto-orbits, the letters appear to wrap with the globe's curvature.
  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    const scene = g.scene && g.scene();
    if (!scene) return;
    const radius = g.getGlobeRadius ? g.getGlobeRadius() : 100;

    const group = new THREE.Group();
    group.name = "hero-globe-wrapped-text";

    const layouts = [
      // TERRITORIA — dead-center over the Pacific (lat=0, lng=-150). Hero element.
      ...layoutWord("TERRITORIA", -150, 6.5, 0).map(l => ({ ...l, font: 240, plane: 17 })),
      // 1/1/27 — directly beneath TERRITORIA. Closer to the equator so the
      // perspective tilt stays minimal and the digits read crisply.
      ...layoutWord("1/1/27", -150, 4.8, -8.5).map(l => ({ ...l, font: 240, plane: 12 })),
    ];

    const meshes = layouts.map(l => {
      const m = makeLetterMesh(l.char, l.font, l.plane);
      placeOnSphere(m, l.lat, l.lng, g, 0.014);
      m.material.opacity = 0; // start invisible — fade in with the entrance
      group.add(m);
      return m;
    });
    scene.add(group);

    // Sync the text fade-in with the camera entrance (~2.4s, ease-out cubic).
    const startedAt = performance.now();
    const FADE_MS = 2400;
    let rafId;
    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / FADE_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      for (let i = 0; i < meshes.length; i++) {
        meshes[i].material.opacity = eased;
      }
      if (t < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      scene.remove(group);
      meshes.forEach(m => {
        m.geometry.dispose();
        m.material.dispose();
      });
    };
  }, [countries.length, size]);

  const HOVER_FILL = "#7df9d6"; // refined aqua-mint highlight (premium replaces the harsh neon)
  const HOVER_STROKE = "#aef7e1";

  return (
    <div ref={wrapRef} className="relative w-full flex justify-center" style={{ height: size }}>
      <div className="absolute inset-0 -m-10 bg-[radial-gradient(circle,_rgba(58,160,255,0.3)_0%,_rgba(58,160,255,0.08)_45%,_transparent_75%)] blur-3xl pointer-events-none" />
      <div className="relative" style={{ width: size, height: size }}>
        <Globe
          ref={globeRef}
          width={size}
          height={size}
          backgroundColor={"rgba(0,0,0,0)"}
          globeMaterial={globeMaterial}
          showAtmosphere={true}
          atmosphereColor={"#3aa0ff"}
          atmosphereAltitude={0.2}
          showGraticules={false}
          polygonsData={countries}
          polygonGeoJsonGeometry={(d) => d.geometry}
          polygonCapColor={(d) => (d === hovered ? HOVER_FILL : colorFor(isoOf(d)))}
          polygonSideColor={() => "rgba(6, 24, 47, 0.92)"}
          polygonStrokeColor={(d) => (d === hovered ? HOVER_STROKE : "rgba(6, 24, 47, 0.95)")}
          polygonAltitude={(d) => (d === hovered ? 0.024 : 0.01)}
          polygonLabel={(d) => {
            const iso = isoOf(d);
            const name = nameOf(d);
            const flagUrl = iso && iso.length === 2
              ? `https://flagcdn.com/h40/${iso}.png`
              : "";
            return `
              <div style="
                display:flex; align-items:center; gap:10px;
                background: linear-gradient(180deg, rgba(10,20,40,0.95) 0%, rgba(2,9,17,0.95) 100%);
                border: 1px solid #3aa0ff;
                box-shadow: 0 0 22px rgba(58,160,255,0.45), inset 0 0 12px rgba(58,160,255,0.18);
                padding: 8px 12px;
                font-family: 'JetBrains Mono', ui-monospace, monospace;
                color: #e6f0ff;
                letter-spacing: 0.12em;
                text-transform: uppercase;
                font-size: 12px;
              ">
                ${flagUrl ? `<img src="${flagUrl}" alt="" style="width:28px; height:auto; border:1px solid rgba(255,255,255,0.25); border-radius:2px;" onerror="this.style.display='none'"/>` : ""}
                <span style="color:#66e6ff; font-weight:600;">${name}</span>
              </div>
            `;
          }}
          onPolygonHover={setHovered}
          polygonsTransitionDuration={220}
        />
      </div>
    </div>
  );
}
